import { Component, Inject, OnInit, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AdminService } from "app/services/admin/admin.service";
import { ImagesService } from "app/services/helper/images.service";
import { ProjectService } from "app/services/project/project.service";
import { VacacionesService } from "app/services/vacaciones/vacaciones.service";
import mime from "mime";
import moment from "app/util/dayjs";
import { map } from "rxjs/operators";

/** Datos que siempre recibe el diálogo de detalles de ausencia */
export interface DialogDetallesAusenciaData {
    idAusencia: number;
    rol: string;
    /** Si true, la UI usa “solicitud” en lugar de “incidencia” / “ausencia” donde aplique. */
    isSolicitud?: boolean;
}

/** Incidencia devuelta por getInfoIncidencia (campos usados en la vista) */
export interface IncidenciaDetalleVista {
    id?: number;
    tipo?: number;
    estado?: number;
    full_day?: number;
    fecha_ini?: string;
    fecha_fin?: string;
    hora_ini?: string | null;
    hora_fin?: string | null;
    creationDate?: string | null;
    dateModeracion?: string | null;
    motivo?: string | null;
    motivo_cancelacion?: string | null;
    tipoName?: string | null;
    subtipoName?: string | null;
    username?: string | null;
    displayName?: string | null;
    checkedBoss?: number | null;
    needDocument?: number | null;
}

@Component({
    selector: "app-dialog-detalles-ausencia",
    templateUrl: "./dialog-detalles-ausencia.component.html",
    styleUrls: ["./dialog-detalles-ausencia.component.scss"],
    standalone: false
})
export class DialogDetallesAusenciaComponent implements OnInit {
    /** Límite por archivo (documentación posterior), en bytes (5 MiB). */
    private static readonly MAX_ARCHIVO_POSTERIOR_BYTES = 5 * 1024 * 1024;

    readonly datosIncidencia = signal<IncidenciaDetalleVista | null>(null);
    readonly isLoading = signal(true);
    readonly loadError = signal(false);

    readonly documentosAnteriores = signal<any[]>([]);
    readonly documentosPosteriores = signal<any[]>([]);
    readonly documentosAdicionales = signal<any[]>([]);

    /** Nombre del archivo elegido en cliente por id de documento de tipología (posterior); sin subida desde este diálogo. */
    archivoPosteriorLocalPorDocId: Record<number, string> = {};
    rol: string = "";

    /**
     * Moderación: 0 elegir acción; 1 confirmar aceptar; 2 motivo y confirmar denegación;
     * 3 confirmar volver a pendiente (solo RRHH).
     */
    stepModeracion: 0 | 1 | 2 | 3 = 0;
    moderacionLoading = false;
    motivoRechazoModeracion = "";

    /**
     * Manager, revisión de documentación posterior: 0 botón principal, 1 doble confirmación.
     */
    pasoValidacionDocsPosterior: 0 | 1 = 0;

    isSolicitud = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogDetallesAusenciaData,
        private dialogRef: MatDialogRef<DialogDetallesAusenciaComponent>,
        private incidentSvc: VacacionesService,
        private projectSvc: ProjectService,
        private imagesSvc: ImagesService,
        private snackBar: MatSnackBar,
        private adminSvc: AdminService
    ) {
        this.rol = this.data.rol;
        this.isSolicitud = !!this.data?.isSolicitud;
    }

    /** Título del diálogo (mat-dialog-title). */
    tituloPrincipal(): string {
        return this.isSolicitud ? "Detalles de la solicitud" : "Detalles de la ausencia";
    }

    mensajeErrorCarga(): string {
        return this.isSolicitud
            ? "No se ha podido cargar la información de la solicitud."
            : "No se ha podido cargar la información de la ausencia.";
    }

    ariaLabelDatosPrincipal(): string {
        return this.isSolicitud ? "Datos de la solicitud" : "Datos de la ausencia";
    }

    /** Bloque documentación entregada al crear el registro. */
    tituloDocumentacionPrevia(): string {
        return this.isSolicitud
            ? "Documentación aportada con la solicitud"
            : "Documentación con la solicitud (antes de la incidencia)";
    }

    ariaLabelDocumentacionPrevia(): string {
        return this.isSolicitud ? "Documentación de la solicitud" : "Documentación previa";
    }

    mensajeSinDocumentosPorTipo(): string {
        return this.isSolicitud
            ? "No hay documentos configurados para este tipo de solicitud."
            : "No hay documentos configurados para este tipo de ausencia.";
    }

    tituloDocumentacionPosterior(): string {
        return this.isSolicitud
            ? "Documentación a adjuntar después de la solicitud"
            : "Documentación a adjuntar después de la incidencia";
    }

    ariaLabelDocumentacionPosterior(): string {
        return this.isSolicitud ? "Documentación posterior a la solicitud" : "Documentación posterior";
    }

    mensajeSinDocumentosPosterioresPorTipo(): string {
        return this.isSolicitud
            ? "No hay documentos posteriores configurados para este tipo de solicitud."
            : "No hay documentos posteriores configurados para este tipo de ausencia.";
    }

    mensajeConfirmarValidacionDocumentacion(): string {
        return this.isSolicitud
            ? "Se va a registrar la validación de la documentación de esta solicitud. Asegúrate de que los archivos mostrados son correctos y completos. ¿Deseas confirmar?"
            : "Se va a registrar la validación de la documentación de esta incidencia. Asegúrate de que los archivos mostrados son correctos y completos. ¿Deseas confirmar?";
    }

    ariaLabelPanelModeracion(): string {
        return this.isSolicitud ? "Moderar solicitud" : "Moderar incidencia";
    }

    mensajeModeracionAceptar(): string {
        return this.isSolicitud ? "¿Deseas aceptar esta solicitud?" : "¿Deseas aceptar esta incidencia?";
    }

    mensajeModeracionDenegar(): string {
        return this.isSolicitud ? "¿Deseas denegar esta solicitud?" : "¿Deseas denegar esta incidencia?";
    }

    mensajeModeracionVolverPendiente(): string {
        return this.isSolicitud
            ? "¿Deseas volver a dejar esta solicitud en estado pendiente?"
            : "¿Deseas volver a dejar esta incidencia en estado pendiente?";
    }

    etiquetaTituloMotivo(): string {
        return this.isSolicitud ? "Motivo de la solicitud" : "Motivo";
    }

    ariaLabelSeccionMotivo(): string {
        return this.isSolicitud ? "Motivo de la solicitud" : "Motivo de la ausencia";
    }

    textoBotonValidarDocumentacion(): string {
        return this.isSolicitud
            ? "He revisado la documentación de la solicitud y es correcta"
            : "He revisado los documentos y son correctos";
    }

    ngOnInit(): void {
        this.initial();
    }

    initial() {
        this.isLoading.set(true);
        this.loadError.set(false);
        this.incidentSvc.getInfoIncidencia(this.data?.idAusencia).subscribe({
            next: (res: any) => {
                this.datosIncidencia.set((res?.incidencia ?? res) as IncidenciaDetalleVista);
                this.documentosAnteriores.set(res?.documentos?.anteriores ?? []);
                this.documentosPosteriores.set(res?.documentos?.posteriores ?? []);
                this.documentosAdicionales.set(res?.documentosAdicionales ?? []);
                this.isLoading.set(false);
                this.archivoPosteriorLocalPorDocId = {};
                this.stepModeracion = 0;
                this.motivoRechazoModeracion = "";
                this.moderacionLoading = false;
                this.pasoValidacionDocsPosterior = 0;
            },
            error: () => {
                this.isLoading.set(false);
                this.loadError.set(true);
            }
        });
    }

    getImageUser(nickname: string | null | undefined): string {
        return this.imagesSvc.getImagePerson(nickname ?? "", Number(this.projectSvc.getProjectId()));
    }

    estadoTexto(estado: number | undefined): string {
        switch (estado) {
            case 0:
                return "Pendiente";
            case 1:
                return "Aceptado";
            case 2:
                return "Cancelado";
            default:
                return "—";
        }
    }

    formatDateTime(value: string | null | undefined): string {
        if (value == null || value === "") {
            return "—";
        }
        const m = moment(value);
        return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : String(value);
    }

    formatDate(value: string | null | undefined): string {
        if (value == null || value === "") {
            return "—";
        }
        const m = moment(value);
        return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
    }

    /** Hora HH:mm desde campo hora (puede venir como "16:57:00") */
    private horaCorta(hora: string | null | undefined): string {
        if (hora == null || hora === "") {
            return "";
        }
        const s = String(hora).trim();
        return s.length >= 5 ? s.substring(0, 5) : s;
    }

    periodoInicio(inc: IncidenciaDetalleVista | null): string {
        if (!inc) {
            return "—";
        }
        const fecha = this.formatDate(inc.fecha_ini);
        if (Number(inc.full_day) === 1) {
            return fecha;
        }
        const h = this.horaCorta(inc.hora_ini ?? undefined);
        return h ? `${fecha} ${h}` : fecha;
    }

    periodoFin(inc: IncidenciaDetalleVista | null): string {
        if (!inc) {
            return "—";
        }
        const fecha = this.formatDate(inc.fecha_fin);
        if (Number(inc.full_day) === 1) {
            return fecha;
        }
        const h = this.horaCorta(inc.hora_fin ?? undefined);
        return h ? `${fecha} ${h}` : fecha;
    }

    mostrarSubtipo(inc: IncidenciaDetalleVista | null): boolean {
        const n = inc?.subtipoName;
        return n != null && String(n).trim() !== "";
    }

    /** Documento de tipología (anteriores): filas devueltas por getInfoIncidencia */
    esDocObligatorio(doc: any): boolean {
        return Number(doc?.obligatorio) === 1;
    }

    tieneAdjunto(doc: any): boolean {
        const h = doc?.hash;
        return h != null && String(h).trim() !== "";
    }

    nombreArchivoMostrado(doc: any): string {
        const n = doc?.nombreDocumento?.trim();
        if (n) {
            return n;
        }
        const f = doc?.fileName?.trim();
        if (f) {
            return f.split("/").pop() || f;
        }
        return "Documento";
    }

    /** Fecha de subida en documentos adicionales (API puede usar creartionDate). */
    fechaDocAdicional(doc: any): string | null {
        const raw = doc?.creationDate ?? doc?.creartionDate;
        if (raw == null || raw === "") {
            return null;
        }
        return this.formatDateTime(raw);
    }

    /** Incidencia pendiente: se puede adjuntar o sustituir documentación posterior desde la UI. */
    puedeGestionarDocumentacionPosterior(): boolean {
        return Number(this.datosIncidencia()?.estado) === 1 && this.rol === "USUARIO";
    }

    esRolManager(): boolean {
        return (
            String(this.rol ?? "")
                .trim()
                .toLowerCase() === "manager"
        );
    }

    esRolRRHH(): boolean {
        return (
            String(this.rol ?? "")
                .trim()
                .toLowerCase() === "rrhh"
        );
    }

    /** Misma condición que el rótulo «(Revisar documentación)» para manager / RRHH en el título. */
    mostrarFlujoRevisarDocumentacionPosterior(): boolean {
        const inc = this.datosIncidencia();
        if (!inc || (!this.esRolManager() && !this.esRolRRHH()) || inc.estado != 1) {
            return false;
        }
        return Number(inc.checkedBoss) === 0 && Number(inc.needDocument) === 0;
    }

    iniciarConfirmacionValidarDocumentosPosterior(): void {
        this.pasoValidacionDocsPosterior = 1;
    }

    cancelarConfirmacionValidarDocumentosPosterior(): void {
        this.pasoValidacionDocsPosterior = 0;
    }

    confirmarValidacionDocumentosIncidencia(): void {
        this.incidentSvc.checkDocumentsIncidencia(this.datosIncidencia()?.id).subscribe({
            next: () => {
                this.initial();
            },
            error: (err: any) => {
                console.error(err);
            }
        });
    }

    /** Manager: solo con solicitud pendiente (estado 0). */
    mostrarAccionesModeracionManager(): boolean {
        return (
            !this.isLoading() &&
            !this.loadError() &&
            this.datosIncidencia() != null &&
            Number(this.datosIncidencia()?.estado) === 0 &&
            this.esRolManager()
        );
    }

    /** RRHH: pasar a pendiente / aceptar / denegar visibles siempre que haya datos cargados. */
    mostrarAccionesModeracionRRHH(): boolean {
        return !this.isLoading() && !this.loadError() && this.datosIncidencia() != null && this.esRolRRHH();
    }

    mostrarPanelModeracion(): boolean {
        return this.mostrarAccionesModeracionManager() || this.mostrarAccionesModeracionRRHH();
    }

    iniciarPasoPendiente(): void {
        this.stepModeracion = 3;
    }

    iniciarPasoAceptar(): void {
        this.stepModeracion = 1;
    }

    iniciarPasoRechazar(): void {
        this.stepModeracion = 2;
        this.motivoRechazoModeracion = "";
    }

    cancelarPasosModeracion(): void {
        this.stepModeracion = 0;
        this.motivoRechazoModeracion = "";
    }

    motivoRechazoModeracionValido(): boolean {
        const t = (this.motivoRechazoModeracion ?? "").trim();
        return t.length > 0 && t.length <= 200;
    }

    ejecutarModeracion(estado: 0 | 1 | 2): void {
        const id = this.datosIncidencia()?.id;
        if (id == null || this.moderacionLoading) {
            return;
        }
        if (estado === 2 && !this.motivoRechazoModeracionValido()) {
            return;
        }
        const motivo = estado === 2 ? this.motivoRechazoModeracion.trim() : "";
        this.moderacionLoading = true;
        const moderatedByRrhh = this.esRolRRHH();
        this.incidentSvc.moderarIncidencia(id, estado, motivo, moderatedByRrhh).subscribe({
            next: () => {
                this.moderacionLoading = false;
                this.dialogRef.close({ refresh: true });
            },
            error: () => {
                this.moderacionLoading = false;
            }
        });
    }

    trackIdDocPosterior(_index: number, doc: any): number {
        return doc?.id ?? _index;
    }

    abrirSelectorArchivoPosterior(doc: any): void {
        if (!this.puedeGestionarDocumentacionPosterior() || doc?.id == null) {
            return;
        }
        const el = document.getElementById(this.idInputArchivoPosterior(doc.id)) as HTMLInputElement | null;
        el?.click();
    }

    idInputArchivoPosterior(docId: number | string): string {
        return `posterior-doc-${String(docId)}`;
    }

    onArchivoPosteriorSeleccionado(doc: any, ev: Event): void {
        const input = ev.target as HTMLInputElement;
        const file = input.files?.[0];
        const id = doc?.id != null ? Number(doc.id) : NaN;
        if (!file || Number.isNaN(id)) {
            return;
        }
        if (file.size > DialogDetallesAusenciaComponent.MAX_ARCHIVO_POSTERIOR_BYTES) {
            this.snackBar.open(
                `El archivo supera el máximo permitido (5 MB). Tamaño: ${(file.size / (1024 * 1024)).toFixed(2)} MB.`,
                "Cerrar",
                { duration: 6000 }
            );
            input.value = "";
            return;
        }
        this.logSeleccionArchivoPosterior(doc, file);
        this.archivoPosteriorLocalPorDocId = {
            ...this.archivoPosteriorLocalPorDocId,
            [id]: file.name
        };
        input.value = "";
    }

    private logSeleccionArchivoPosterior(doc: any, file: File): void {
        const esEdicion = this.tieneAdjunto(doc);
        this.incidentSvc.anadirDocumentoIncidencia(this.data?.idAusencia, doc.id, file).subscribe({
            next: (res: any) => {
                this.initial();
            },
            error: (err: any) => {
                console.error(err);
            }
        });
    }

    nombreArchivoPosteriorPendiente(doc: any): string | null {
        const id = doc?.id != null ? Number(doc.id) : NaN;
        if (Number.isNaN(id)) {
            return null;
        }
        const n = this.archivoPosteriorLocalPorDocId[id];
        return n != null && String(n).trim() !== "" ? n : null;
    }

    onAbrirDocumentoTipologia(doc: any): void {
        if (!this.tieneAdjunto(doc)) {
            return;
        }
        const hash = String(doc.hash).trim();
        const path = String(doc.fileName ?? "");
        const mimeType =
            (typeof doc.contentType === "string" && doc.contentType.trim()) ||
            mime.getType(path) ||
            "application/octet-stream";
        const lower = path.toLowerCase();
        const isWord =
            mimeType === "application/msword" ||
            mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            lower.endsWith(".doc") ||
            lower.endsWith(".docx");

        this.incidentSvc.getOneDocumentIncidencia(hash).subscribe({
            next: (blob: Blob) => {
                const finalBlob = new Blob([blob], { type: mimeType });
                const fileURL = URL.createObjectURL(finalBlob);
                if (isWord) {
                    const a = window.document.createElement("a");
                    a.href = fileURL;
                    a.download = this.nombreArchivoMostrado(doc) || "documento";
                    window.document.body.appendChild(a);
                    a.click();
                    window.document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
                } else {
                    window.open(fileURL, "_blank");
                    setTimeout(() => URL.revokeObjectURL(fileURL), 120_000);
                }
            }
        });
    }

    cerrar(): void {
        this.dialogRef.close();
    }

    showAdjunto(documentId: number, path: string) {
        const mimeType = mime.getType(path) || "application/octet-stream";
        this.adminSvc
            .getOneDocumentIncidencia(documentId)
            .pipe(map((response: any) => new Blob([response], { type: mimeType })))
            .subscribe((blob: Blob) => {
                const fileURL = URL.createObjectURL(blob);
                window.open(fileURL, "_blank");
            });
    }
}
